// IndexedDB vault for Live/Motion media captured while offline. Clips are
// stored on-device, tokens carry `video_id: "offline:<id>"`, and an axios
// request interceptor swaps in the real server id right before any request
// that ships photos — so queued offline actions sync transparently.
import { api } from "@/lib/api";
import { colonyApi } from "@/lib/colonyApi";
import {
  LIVE_PHOTO_PREFIX,
  parseMediaValue,
  uploadLiveMotion,
  ingestMotionPhoto,
} from "@/lib/livePhotoMedia";

export const OFFLINE_VIDEO_PREFIX = "offline:";
const DB_NAME = "offline_live_media";
const STORE = "motions";

const isNetErr = (e) => !e?.response || e?.code === "ERR_NETWORK" || e?.code === "ECONNABORTED";

const openDb = () =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "id" });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const reqDone = (r) =>
  new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });

const withStore = async (mode, fn) => {
  const dbi = await openDb();
  try {
    const t = dbi.transaction(STORE, mode);
    const out = await fn(t.objectStore(STORE));
    await new Promise((resolve, reject) => {
      t.oncomplete = resolve;
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
    return out;
  } finally {
    dbi.close();
  }
};

export async function saveOfflineMotion(rec) {
  const id = `olm-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  await withStore("readwrite", (s) => reqDone(s.put({ ...rec, id, at: Date.now() })));
  return id;
}
export const getOfflineMotion = (id) => withStore("readonly", (s) => reqDone(s.get(id)));
export const putOfflineMotion = (rec) => withStore("readwrite", (s) => reqDone(s.put(rec)));
export const deleteOfflineMotion = (id) => withStore("readwrite", (s) => reqDone(s.delete(id)));
export const listOfflineMotions = () => withStore("readonly", (s) => reqDone(s.getAll()));

const clientFor = (rec) => (rec.portal === "colony" ? colonyApi : api);

const isOfflineTokenString = (p) =>
  typeof p === "string" && p.startsWith(LIVE_PHOTO_PREFIX) && p.includes(OFFLINE_VIDEO_PREFIX);

const offlineLocalId = (value) => {
  const media = parseMediaValue(value);
  if (!media.live || !String(media.video_id).startsWith(OFFLINE_VIDEO_PREFIX)) return null;
  return String(media.video_id).slice(OFFLINE_VIDEO_PREFIX.length);
};

async function uploadRecord(rec) {
  const client = clientFor(rec);
  let uploaded;
  if (rec.kind === "ingest") {
    const file = new File([rec.blob], rec.filename || "motion-photo.jpg", { type: rec.contentType || "image/jpeg" });
    const token = await ingestMotionPhoto(file, { client, scope: rec.scope, colonyId: rec.colonyId });
    uploaded = { kind: "ingest", token };
  } else {
    const file = new File([rec.blob], rec.filename || "live-motion.mp4", { type: rec.contentType || "video/mp4" });
    const up = await uploadLiveMotion(file, {
      client, scope: rec.scope, colonyId: rec.colonyId, sharedAcrossColonies: !!rec.sharedAcrossColonies,
    });
    uploaded = { kind: "motion", id: up.id, content_type: up.content_type };
  }
  const next = { ...rec, uploaded };
  await putOfflineMotion(next);
  return next;
}

// Turn an offline token into a real server token (uploading now if needed).
// Returns the token unchanged when we're still offline (caller stays queued).
export async function materializeOfflineToken(value) {
  const media = parseMediaValue(value);
  const localId = offlineLocalId(value);
  if (!localId) return value;
  let rec = null;
  try { rec = await getOfflineMotion(localId); } catch { /* IDB unavailable */ }
  if (!rec) return media.image || ""; // clip lost — keep the still
  if (!rec.uploaded) {
    try {
      rec = await uploadRecord(rec);
    } catch (e) {
      if (isNetErr(e)) return value; // still offline — retried on next sync
      return media.image || ""; // server rejected the clip — keep the still
    }
  }
  if (rec.uploaded.kind === "ingest") return rec.uploaded.token;
  return `${LIVE_PHOTO_PREFIX}${JSON.stringify({
    image: media.image,
    video_id: rec.uploaded.id,
    video_type: rec.uploaded.content_type,
    filename: media.filename || "live-photo",
  })}`;
}

// Proactively upload every pending offline clip (called when back online).
export async function syncOfflineMotions() {
  if (typeof navigator !== "undefined" && !navigator.onLine) return;
  let recs = [];
  try { recs = (await listOfflineMotions()) || []; } catch { return; }
  for (const rec of recs) {
    if (rec.uploaded) continue;
    try { await uploadRecord(rec); } catch { /* retry on next sync */ }
  }
}

// Drop uploaded clips no longer referenced by the offline queue, and anything
// stale beyond 14 days.
export async function cleanupOfflineMotions(remainingQueue) {
  const referenced = new Set();
  for (const item of remainingQueue || []) {
    for (const p of item?.body?.photos || []) {
      const id = offlineLocalId(p);
      if (id) referenced.add(id);
    }
  }
  let recs = [];
  try { recs = (await listOfflineMotions()) || []; } catch { return; }
  const HOUR = 3600 * 1000;
  for (const rec of recs) {
    if (referenced.has(rec.id)) continue;
    const age = Date.now() - (rec.at || 0);
    if ((rec.uploaded && age > HOUR) || age > 14 * 24 * HOUR) {
      try { await deleteOfflineMotion(rec.id); } catch { /* ignore */ }
    }
  }
}

// Rewrite offline tokens right before ANY request that ships photos — covers
// both live submits (composer kept an offline token) and queue replays.
const needsRewrite = (data) =>
  data && typeof data === "object" && !Array.isArray(data) &&
  Array.isArray(data.photos) && data.photos.some(isOfflineTokenString);

for (const client of [api, colonyApi]) {
  client.interceptors.request.use(async (config) => {
    try {
      if (needsRewrite(config.data)) {
        const photos = [];
        for (const p of config.data.photos) {
          photos.push(isOfflineTokenString(p) ? await materializeOfflineToken(p) : p);
        }
        config.data = { ...config.data, photos };
      }
    } catch { /* never block the request */ }
    return config;
  });
}
